/**
 * URL validation regex that matches:
 * - Domains with or without protocol (http://, https://)
 * - Domains with or without www prefix
 * - Domain names 2-256 chars long with alphanumeric chars and `@:%._+~#?&//=`
 * - Top level domains between 2-6 chars (.com, .org, .co.uk etc)
 * - Optional paths and query params after domain
 *  - URLs with hyphens in domain name
 *
 * Valid examples:
 * - google.com
 * - www.google.com
 * - http://google.com
 * - https://google.com/search?q=test
 * - mydomain.org/path?param=value#fragment
 * - sub.example.co.uk
 * - https://support-portal-contentstack.matrix-uat.unifyapps.com
 *
 * Invalid:
 * - http:// (no domain)
 * - a.b (domain/TLD too short)
 * - invalid url text
 * - http://toolong.invalidtld
 * - missing.tld/
 * - /path/to/resource (standalone path)
 * - path/to/resource (standalone path)
 */

export const URL_REGEX =
  /^(?:https?:\/\/)?(?:www\.)?[\w#%&+./:=?@~-]{2,256}\.[a-z]{2,6}\b(?:[\w#%&+./:=?@~-]*)?$/;

// These are used in go to url action to open the corresponding app.
// For eg. tel: 9812345678, mailto: support@unifyapps.com
export const SPECIAL_URI_SCHEMES = ['sms:', 'tel:', 'mailto:', 'geo:'];

/**
 * Matches a path with optional query params
 * ex. `example`, `path/to`, `path/to/`, `path/to/something-with-dashes/`,
 * `path/to/something-with-dashes?query=another-thing`, `path/to/something-with-dashes?query=another&foo=bar`
 *
 * Does not match urls
 * ex. `example.com`, `google.com/something`,
 */
// eslint-disable-next-line prefer-named-capture-group -- its okay
export const PATH_REGEX = /^([\w-]+\/)*[\w-]+\/?(\?[\w&=-]*)?$/;

/**
 * 1. if the url begins with `http://` or `https://` or `//`, url is complete, do nothing
 * 2. if the url begins with special uri schemes mentioned above, url is complete, do nothing
 * 3. url begins with `../` or `./` or `/`, url is just path, do nothing
 * 4. if the url is a path, treat as relative path and do nothing
 * 5. if appendHttp is true, append `http://` to the url,
 * 6. otherwise append `//` to the url,
 */
export function adaptComputedPathOrUrl(path: string | undefined, appendHttp?: boolean) {
  if (!path) return path;

  if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('//')) {
    return path;
  }

  if (SPECIAL_URI_SCHEMES.some((scheme) => path.startsWith(scheme))) {
    return path;
  }

  if (path.startsWith('../') || path.startsWith('./') || path.startsWith('/')) {
    return path;
  }

  if (PATH_REGEX.test(path)) {
    return path;
  }

  // Appending http:// rather than just // so that link looks good in the LinkPreviewPanel
  // Slack and Monday also appends http:// to path without protocols
  if (appendHttp) {
    return `http://${path}`;
  }

  return `//${path}`;
}
